import { LoadingOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Spin, Upload } from "antd";
import TextArea from "antd/lib/input/TextArea";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { VALIDATION_MESSAGE_CONFIG } from '../../../../../constants/constants';
import { FileMDModel, ScenarioFrontierFileModel, ScenarioFrontierUploadFileModel } from '../../../../../models/scenario-frontier.model';
import { ScenarioFrontierService } from '../../../../../services/scenario-frontier.service';
import { DATE_UTIL } from '../../../../../utils/dateUtils';
import UTIL from '../../../../../utils/utils';
import { ClearIcon, InfoIcon, PaperIcon, PaperPlusIcon, UploadIcon } from '../../../../atoms/icon';
import InputTable from '../../../ecap-module/shared/input-table';
import "./index.scss";

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

interface Props {
    uploadFile: (model: ScenarioFrontierUploadFileModel) => void;
    fileData: ScenarioFrontierFileModel | undefined;
    loading: boolean;
    errorMessage: string | undefined;
    deleteFile: () => void;
}

const FrontierUploadFile = (props: Props) => {

    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [file, setFile] = useState<File>();
    const [inputData, setInputData] = useState<any[]>();
    const [fileMd, setFileMd] = useState<FileMDModel>();

    const formSubmit = () => {
    }

    const dummyRequest = (options: any) => {
        setTimeout(() => {
            options.onSuccess("ok");
        }, 0);
    };

    const handleupload = (file: File) => {
        if (!form.getFieldValue("FileName")) {
            form.setFieldsValue({ FileName: file.name });
        }
        uploadFile(file);
        return "";
    }

    const clearFile = async () => {
        await props.deleteFile();
        setFileMd(undefined);
        setFile(undefined);
        setInputData(undefined);
    }

    const confirmClearFile = () => {
        Modal.confirm({
            content: 'Do you want to delete this file?',
            onOk: clearFile,
            icon: '',
            width: 304
        });
    }

    const uploadFile = (file: File) => {
        if (file) {
            setFile(file);
            const values: ScenarioFrontierUploadFileModel = { ...form.getFieldsValue() };
            values.file = file;
            props.uploadFile(values);
        }
    }

    const downloadSampleFile = useCallback(() => {
        ScenarioFrontierService.downloadSampleFile().then(res => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `SFSB_INPUT.csv`); //or any other extension
            document.body.appendChild(link);
            link.click();
        });
    }, [])

    useEffect(() => {

        if (props.fileData) {

            if (!form.getFieldValue("FileName") && props.fileData.name) {
                form.setFieldsValue({ FileName: props.fileData.name });
            }

            if (!form.getFieldValue("Note") && props.fileData.note) {
                form.setFieldsValue({ Note: props.fileData.note });
            }

            if (props.fileData.file_data) {
                let data = [];
                try {
                    data = JSON.parse(props.fileData.file_data);
                    if (data && data.length > 10) {
                        data = data.slice(0, 10);
                    }
                    setInputData(data);
                } catch {
                    console.error("can not parse fileData");
                }
            } else {
                setInputData(undefined);
            }

            if (props.fileData.file_md) {
                try {
                    const data = JSON.parse(props.fileData.file_md);
                    setFileMd(data);
                } catch (error) {
                    console.error(error);
                }
            }
        }

    }, [form, props.fileData])

    return (
        <React.Fragment>
            <div className="frontier-upload-file">

                {
                    <React.Fragment>
                        <div className="step-content-item-header">
                            <PaperPlusIcon className="upload-header-icon" />
                            <h4>
                                {t("ScenarioFrontier.lbCreditStressTestInput")}
                            </h4>
                            <p className="des-header">
                                {t("ScenarioFrontier.lbUploadFileDes")}&nbsp;
                                {t("ScenarioFrontier.lbClickDownload")}&nbsp;
                                <span className="text-underline blue-text action-text" onClick={downloadSampleFile}>
                                    {t("ScenarioFrontier.lbSampleFile")}
                                </span>
                            </p>

                            {
                                file && <div className={`file-item-wrapper${!!props.errorMessage ? " file-error" : ""}`}>
                                    <Spin spinning={props.loading} indicator={antIcon} >
                                        <div className="file-item">
                                            <div>
                                                <span>
                                                    <PaperIcon />
                                                    {file?.name}
                                                </span>
                                                <span className="mr-l-25">
                                                    {t("RapmReport.lbCsvFile")} &middot; {UTIL.bytesToSize(file?.size)} &middot; {t("RapmReport.lbAddedOn")} {DATE_UTIL.CurrentDate()}
                                                </span>
                                            </div>

                                            <Button shape="circle" className="clear-button" onClick={confirmClearFile}>
                                                <ClearIcon />
                                            </Button>
                                        </div>

                                    </Spin>
                                </div>
                            }

                            {
                                (!file && fileMd) && <div className={`file-item-wrapper${!!props.errorMessage ? " file-error" : ""}`}>
                                    <Spin spinning={props.loading} indicator={antIcon} >
                                        <div className="file-item">
                                            <div>
                                                <span>
                                                    <PaperIcon />
                                                    {fileMd.name}
                                                </span>
                                                <span className="mr-l-25">
                                                    {t("RapmReport.lbCsvFile")} &middot; {UTIL.bytesToSize(fileMd.length)} &middot; {t("RapmReport.lbAddedOn")} {DATE_UTIL.convertServerToViewFormat(props.fileData?.created_on)}
                                                </span>
                                            </div>

                                            <Button shape="circle" className="clear-button" onClick={confirmClearFile}>
                                                <ClearIcon />
                                            </Button>
                                        </div>

                                    </Spin>
                                </div>
                            }

                            {
                                !!props.errorMessage && <span className="text-error">
                                    <span><InfoIcon /></span> <span>{props.errorMessage}</span>
                                </span>
                            }

                            <Upload
                                multiple={false}
                                action={handleupload}
                                maxCount={1}
                                listType="picture"
                                accept=".csv"
                                className="upload-file"
                                showUploadList={false}
                                customRequest={dummyRequest}>
                                {
                                    ((!file || !!props.errorMessage) && !fileMd) && <Button icon={<UploadIcon />} type="primary" shape="round">{(t("RapmReport.lbUploadFile"))}</Button>
                                }
                            </Upload>
                        </div>

                        <Form
                            layout='vertical'
                            validateMessages={VALIDATION_MESSAGE_CONFIG}
                            onFinish={(values) => formSubmit()}
                            scrollToFirstError
                            form={form}>


                            <Form.Item
                                label={t("RapmReport.lbFileName")}
                                name="FileName">
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label={t("RapmReport.lbFileNotes")}
                                name="Note">
                                <TextArea rows={5} />
                            </Form.Item>
                        </Form>
                    </React.Fragment>
                }

            </div >

            {
                (inputData && inputData.length) &&
                <InputTable columns={fileMd?.columns} data={inputData} file={file} />
            }

        </React.Fragment >
    )
}

export default FrontierUploadFile;